// src/components/orders/OrderTicketsModal.tsx
import React, { useEffect, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import ConfirmModal from "../ui/ConfirmModal";
import { Send, Download } from "lucide-react";
import { OrderItem } from "../../services/types/orders.types";
import {
    getOrderTicketsRequest,
    sendOrderTicketsRequest,
    sendOrderSingleTicketRequest,
    downloadOrderInvoicePdfRequest,
    downloadOrderTicketsPdfRequest,
    downloadOrderLieferscheinPdfRequest,
    triggerBrowserDownload,
    removeOrderSeatRequest,
    TicketItem,
    RelocationResult,
} from "../../services/api/orders.api";
import OrderRelocationModal from "./OrderRelocationModal";

interface OrderTicketsModalProps {
    isOpen: boolean;
    order: OrderItem | null;
    onClose: () => void;
    // Вызывается после успешного удаления билета — чтобы родительский список заказов
    // обновил у себя новые total_price/discount_value этого заказа (как ApplyDiscountModal.onSubmit).
    onOrderUpdated?: (updated: OrderItem) => void;
}

const OrderTicketsModal: React.FC<OrderTicketsModalProps> = ({
    isOpen,
    order,
    onClose,
    onOrderUpdated,
}) => {
    const [tickets, setTickets] = useState<TicketItem[]>([]);
    const [customerEmail, setCustomerEmail] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [sendingAll, setSendingAll] = useState(false);
    const [sendingTicketNo, setSendingTicketNo] = useState<number | null>(null);
    const [actionMessage, setActionMessage] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [downloadingKind, setDownloadingKind] = useState<
        "invoice" | "tickets" | "lieferschein" | null
    >(null);

    // Билет, который админ собирается удалить из заказа — до подтверждения в ConfirmModal.
    const [seatPendingRemoval, setSeatPendingRemoval] = useState<TicketItem | null>(null);
    const [removingSeatId, setRemovingSeatId] = useState<string | null>(null);

    // Режим пересадки (выбор нового мероприятия/города/мест).
    const [isRelocationOpen, setIsRelocationOpen] = useState(false);

    useEffect(() => {
        if (!isOpen || !order) {
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        setLoadError(null);
        setActionMessage(null);
        setActionError(null);
        setSeatPendingRemoval(null);

        getOrderTicketsRequest(order.id)
            .then((res) => {
                if (cancelled) return;
                setTickets(res.tickets || []);
                setCustomerEmail(res.customer_email || null);
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadError(err?.message || "Не удалось загрузить билеты");
                setTickets([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, order]);

    if (!isOpen || !order) return null;

    // Пересадка недоступна для отменённых, возвращённых, партнёрских и API-заказов.
    const relocationBlockedReason = order.is_deleted
        ? "Заказ отменён"
        : order.is_refunded
        ? "По заказу оформлен возврат"
        : order.is_partner_order
        ? "Партнёрский заказ (Artist Production)"
        : order.is_api_order
        ? "Заказ через API распространителя"
        : null;

    // Партнёрский заказ (Artist Production / Nashe): настоящие штрихкоды партнёр выдаёт
    // только после оплаты — до отметки «оплачен» билеты не скачиваются и не отправляются
    // (бэкенд тоже отказывает).
    const partnerTicketsLocked = Boolean(order.is_partner_order && !order.is_paid);
    const partnerTicketsLockedText =
        "Заказ партнёра не оплачен: билеты (штрихкоды партнёра) будут доступны после отметки оплаты";

    const handleRelocated = async (res: RelocationResult) => {
        setIsRelocationOpen(false);
        setActionError(null);
        const sentText =
            res.notification === "tickets"
                ? ` Новые билеты отправлены на ${res.notification_sent_to || "email клиента"}.`
                : res.notification === "invoice"
                ? ` Обновлённый счёт отправлен${res.notification_sent_to ? ` на ${res.notification_sent_to}` : ""}.`
                : "";
        setActionMessage(`Пересадка выполнена.${sentText}${res.warning ? ` ${res.warning}` : ""}`);
        onOrderUpdated?.(res.order);
        try {
            const ticketsRes = await getOrderTicketsRequest(order.id);
            setTickets(ticketsRes.tickets || []);
            setCustomerEmail(ticketsRes.customer_email || null);
        } catch (err: any) {
            setActionError(err?.message || "Не удалось обновить список билетов");
        }
    };

    const handleSendAll = async () => {
        setActionError(null);
        setActionMessage(null);
        setSendingAll(true);
        try {
            await sendOrderTicketsRequest(order.id);
            setActionMessage(`Все билеты отправлены на ${customerEmail || "email клиента"}`);
        } catch (err: any) {
            setActionError(err?.message || "Не удалось отправить билеты");
        } finally {
            setSendingAll(false);
        }
    };

    const handleDownload = async (
        kind: "invoice" | "tickets" | "lieferschein",
    ) => {
        if (!order) return;
        setActionError(null);
        setActionMessage(null);
        setDownloadingKind(kind);
        try {
            const req =
                kind === "invoice"
                    ? downloadOrderInvoicePdfRequest
                    : kind === "tickets"
                    ? downloadOrderTicketsPdfRequest
                    : downloadOrderLieferscheinPdfRequest;
            const { blob, filename } = await req(order.id);
            triggerBrowserDownload(blob, filename);
        } catch (err: any) {
            setActionError(err?.message || "Не удалось скачать PDF");
        } finally {
            setDownloadingKind(null);
        }
    };

    const handleSendOne = async (ticketNo: number) => {
        setActionError(null);
        setActionMessage(null);
        setSendingTicketNo(ticketNo);
        try {
            await sendOrderSingleTicketRequest(order.id, ticketNo);
            setActionMessage(
                `Билет #${ticketNo} отправлен на ${customerEmail || "email клиента"}`,
            );
        } catch (err: any) {
            setActionError(err?.message || `Не удалось отправить билет #${ticketNo}`);
        } finally {
            setSendingTicketNo(null);
        }
    };

    const handleRemoveSeatConfirmed = async () => {
        if (!order || !seatPendingRemoval || !seatPendingRemoval.hall_seat_price_id) {
            setSeatPendingRemoval(null);
            return;
        }
        const seatId = seatPendingRemoval.hall_seat_price_id;
        setActionError(null);
        setActionMessage(null);
        setRemovingSeatId(seatId);
        try {
            const updatedOrder = await removeOrderSeatRequest(order.id, seatId);
            // Список билетов запрашиваем заново — сумма/скидка и оставшиеся билеты
            // могли измениться (пересчёт скидки на бэкенде).
            const ticketsRes = await getOrderTicketsRequest(order.id);
            setTickets(ticketsRes.tickets || []);
            setCustomerEmail(ticketsRes.customer_email || null);
            setActionMessage(`Билет удалён из заказа ${order.order_number}`);
            onOrderUpdated?.(updatedOrder);
        } catch (err: any) {
            setActionError(err?.message || "Не удалось удалить билет из заказа");
        } finally {
            setRemovingSeatId(null);
            setSeatPendingRemoval(null);
        }
    };

    return (
        <>
        <div className="modal-backdrop">
            <div
                className="modal-dialog order-tickets-dialog"
                style={{ maxWidth: 1180 }}
            >
                <Card>
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                Билеты заказа {order.order_number}
                            </h3>
                            <p className="card-text">
                                Email клиента: {customerEmail || "—"}
                            </p>
                        </div>
                        <div
                            className="inline-actions"
                            style={{ flexWrap: "wrap", gap: 8 }}
                        >
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => handleDownload("invoice")}
                                disabled={downloadingKind !== null}
                                title="Скачать счёт (PDF)"
                            >
                                {downloadingKind === "invoice"
                                    ? "Загрузка..."
                                    : "Счёт"}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => handleDownload("tickets")}
                                disabled={downloadingKind !== null || partnerTicketsLocked}
                                title={partnerTicketsLocked ? partnerTicketsLockedText : "Скачать билеты (PDF)"}
                            >
                                {downloadingKind === "tickets"
                                    ? "Загрузка..."
                                    : "Билеты"}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => handleDownload("lieferschein")}
                                disabled={downloadingKind !== null}
                                title="Скачать лифершайн (PDF)"
                            >
                                {downloadingKind === "lieferschein"
                                    ? "Загрузка..."
                                    : "Лифершайн"}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setIsRelocationOpen(true)}
                                disabled={!!relocationBlockedReason || isLoading || tickets.length === 0}
                                title={relocationBlockedReason || "Пересадить клиента на другие места"}
                            >
                                Пересадка
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={handleSendAll}
                                disabled={
                                    sendingAll ||
                                    sendingTicketNo !== null ||
                                    isLoading ||
                                    tickets.length === 0 ||
                                    partnerTicketsLocked
                                }
                                title={partnerTicketsLocked ? partnerTicketsLockedText : undefined}
                            >
                                {sendingAll ? "Отправляем..." : "Отправить все билеты"}
                            </Button>
                        </div>
                    </div>

                    {partnerTicketsLocked && (
                        <div className="form-global-error" style={{ marginBottom: 8 }}>
                            {partnerTicketsLockedText}. Скачать и отправить билеты можно, когда заказ
                            будет отмечен оплаченным.
                        </div>
                    )}
                    {actionMessage && (
                        <div className="form-global-success" style={{ marginBottom: 8 }}>
                            {actionMessage}
                        </div>
                    )}
                    {actionError && (
                        <div className="form-global-error" style={{ marginBottom: 8 }}>
                            {actionError}
                        </div>
                    )}

                    {isLoading ? (
                        <p>Загружаем билеты...</p>
                    ) : loadError ? (
                        <div className="form-global-error">{loadError}</div>
                    ) : tickets.length === 0 ? (
                        <p>В этом заказе нет билетов.</p>
                    ) : (
                        <div className="orders-tickets-list orders-tickets-scroll">
                            <table className="table" style={{width: "100%"}}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Тур / Город</th>
                                        <th>Дата</th>
                                        <th>Сектор / Ряд / Место</th>
                                        <th>Цена</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.map((t) => (
                                        <tr key={t.ticket_no}>
                                            <td>{t.ticket_no}</td>
                                            <td>
                                                <div>{t.tour_name || "—"}</div>
                                                <div className="table-secondary-text">
                                                    {t.city_name || "—"}
                                                    {t.hall_name ? ` • ${t.hall_name}` : ""}
                                                </div>
                                            </td>
                                            <td>{t.event_date || "—"}</td>
                                            <td>
                                                {t.sector_name || "—"} / {t.row_name ?? "—"} /{" "}
                                                {t.seat_name ?? "—"}
                                            </td>
                                            <td>
                                                {t.price ? `${t.price} €` : "—"}
                                            </td>
                                            <td>
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    onClick={() => handleSendOne(t.ticket_no)}
                                                    disabled={
                                                        sendingAll ||
                                                        sendingTicketNo !== null ||
                                                        partnerTicketsLocked
                                                    }
                                                    title={partnerTicketsLocked ? partnerTicketsLockedText : undefined}
                                                >
                                                    {sendingTicketNo === t.ticket_no
                                                        ? "..."
                                                        : "Отправить"}
                                                </Button>
                                                {!order.is_paid && !order.is_paid_by_distributor && !order.is_partner_order && t.hall_seat_price_id && (
                                                    <Button
                                                        type="button"
                                                        variant="danger"
                                                        style={{ marginLeft: 8 }}
                                                        onClick={() => setSeatPendingRemoval(t)}
                                                        disabled={
                                                            sendingAll ||
                                                            sendingTicketNo !== null ||
                                                            removingSeatId !== null
                                                        }
                                                        title="Удалить билет из заказа"
                                                    >
                                                        {removingSeatId === t.hall_seat_price_id
                                                            ? "..."
                                                            : "Удалить"}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="editor-actions" style={{ marginTop: 12 }}>
                        <Button type="button" variant="ghost" onClick={onClose}>
                            Закрыть
                        </Button>
                    </div>
                </Card>
            </div>
        </div>

        {/* подтверждение удаления одного билета из заказа */}
        <ConfirmModal
            isOpen={!!seatPendingRemoval}
            title="Удалить билет из заказа?"
            description={
                seatPendingRemoval
                    ? `Билет #${seatPendingRemoval.ticket_no} (${seatPendingRemoval.sector_name || "—"} / ${seatPendingRemoval.row_name ?? "—"} / ${seatPendingRemoval.seat_name ?? "—"}) будет удалён из заказа, а сумма и скидка заказа будут пересчитаны. Действие нельзя отменить. Продолжить?`
                    : ""
            }
            confirmLabel={removingSeatId ? "Удаляем..." : "Удалить"}
            cancelLabel="Отмена"
            variant="danger"
            onCancel={() => setSeatPendingRemoval(null)}
            onConfirm={handleRemoveSeatConfirmed}
        />

        <OrderRelocationModal
            isOpen={isRelocationOpen}
            order={order}
            ticketsCount={tickets.length}
            onClose={() => setIsRelocationOpen(false)}
            onRelocated={handleRelocated}
        />
        </>
    );
};

export default OrderTicketsModal;
